/**
 * Нормализованный префикс Vite `base` без завершающего `/`. \
 * При `base = "/"` возвращает пустую строку.
 */
function getAppBasePrefix(): string {
    const base = import.meta.env.BASE_URL;
    const normalized = base.endsWith("/") ? base.slice(0, -1) : base;
    return normalized === "" ? "" : normalized;
}

/**
 * Префиксует путь приложения Vite `base` (саброут деплоя: `/mobile/`, `/` и т.д.). \
 * Абсолютные URL другого origin, `blob:`, `data:`, `mailto:` — без изменений. \
 * Same-origin абсолютные URL без base — base вставляется в pathname.
 *
 * @param url путь (`/auth`), `URL` или абсолютный URL
 * @param win объект окна (для origin)
 * @returns абсолютный href с учётом base или `undefined`, если `url` пустой
 */
export function toAppUrl(url: string | URL | undefined, win: Window): string | undefined {
    if (url == null) {
        return undefined;
    }

    const href = url instanceof URL ? url.href : url;

    if (/^(blob:|data:|mailto:)/i.test(href)) {
        return href;
    }

    const base = import.meta.env.BASE_URL;
    const baseDir = base.endsWith("/") ? base : `${base}/`;
    const basePrefix = getAppBasePrefix();

    // Относительный путь приложения: "/auth", "auth"
    if (!/^[a-z][a-z0-9+.-]*:/i.test(href)) {
        const relative = href.replace(/^\//, "");
        return new URL(relative, `${win.location.origin}${baseDir}`).href;
    }

    const parsed = new URL(href);
    if (parsed.origin !== win.location.origin) {
        return href;
    }

    if (basePrefix.length === 0) {
        return href;
    }
    if (parsed.pathname === basePrefix || parsed.pathname.startsWith(`${basePrefix}/`)) {
        return href;
    }

    parsed.pathname = `${basePrefix}${parsed.pathname}`;
    return parsed.href;
}

/**
 * Убирает Vite `base` из пути, чтобы получить путь для React Router (`basename`). \
 * Например: `/mobile/menu` → `/menu` при `BASE_URL=/mobile/`.
 *
 * @param path путь (`pathname` или `pathname + search + hash`)
 * @returns путь без base-префикса
 */
export function stripAppBasePrefix(path: string): string {
    const basePrefix = getAppBasePrefix();
    if (basePrefix.length === 0) {
        return path;
    }

    if (path === basePrefix || path.startsWith(`${basePrefix}?`) || path.startsWith(`${basePrefix}#`)) {
        const rest = path.slice(basePrefix.length);
        return rest === "" ? "/" : rest;
    }

    if (path.startsWith(`${basePrefix}/`)) {
        return path.slice(basePrefix.length);
    }

    return path;
}

/**
 * Текущий путь приложения из адресной строки без Vite `base` (`pathname + search`). \
 * Подходит для query `redirect` и `navigate` при `BrowserRouter` с `basename`.
 *
 * @param win объект окна
 * @returns router-relative путь
 */
export function getRouterPathFromBrowser(win: Window = window): string {
    return `${stripAppBasePrefix(win.location.pathname)}${win.location.search}`;
}
